// IndexedDB Wrapper for Offline POS Transaction Queue
// Uses idb library for a cleaner Promise-based API

import { openDB } from 'idb';

const DB_NAME = 'sims-offline-db';
const DB_VERSION = 1;
const STORE_NAME = 'pending-transactions';

let dbInstance = null;
let memoryStore = [];
let nextMemoryId = 1;

function isIndexedDBAvailable() {
  try {
    return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined' && window.indexedDB !== null;
  } catch (e) {
    return false;
  }
}

/**
 * Initialize and get the database instance
 */
export async function getDB() {
  if (!isIndexedDBAvailable()) {
    return null;
  }
  if (dbInstance) return dbInstance;
  
  try {
    dbInstance = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('timestamp', 'timestamp');
          store.createIndex('status', 'status');
          store.createIndex('type', 'type');
        }
      },
    });
    return dbInstance;
  } catch (err) {
    console.warn('[OfflineDB] Failed to open IndexedDB, using memory store fallback:', err);
    return null;
  }
}

/**
 * Queue a transaction for offline sync
 * @param {Object} transaction - The transaction data to queue
 * @param {string} transaction.type - Type of transaction (e.g., 'sale', 'refund', 'payment')
 * @param {Object} transaction.data - The actual transaction data
 * @param {Object} transaction.metadata - Additional metadata (device info, user, etc.)
 */
export async function queueTransaction(transaction) {
  const db = await getDB();
  const tx = {
    ...transaction,
    timestamp: Date.now(),
    status: 'pending',
    retryCount: 0,
    lastAttempt: null,
  };
  
  if (db) {
    const id = await db.add(STORE_NAME, tx);
    console.log('[OfflineDB] Transaction queued with ID:', id);
    return id;
  }

  // Memory fallback
  const id = nextMemoryId++;
  const record = { ...tx, id };
  memoryStore.push(record);
  console.log('[OfflineDB] Transaction queued in memory store with ID:', id);
  return id;
}

/**
 * Get all pending transactions
 */
export async function getPendingTransactions() {
  const db = await getDB();
  if (db) {
    return db.getAllFromIndex(STORE_NAME, 'status', 'pending');
  }
  return memoryStore.filter(t => t.status === 'pending');
}

/**
 * Get all failed transactions
 */
export async function getFailedTransactions() {
  const db = await getDB();
  if (db) {
    return db.getAllFromIndex(STORE_NAME, 'status', 'failed');
  }
  return memoryStore.filter(t => t.status === 'failed');
}

/**
 * Get all transactions (including synced/failed)
 */
export async function getAllTransactions() {
  const db = await getDB();
  if (db) {
    return db.getAll(STORE_NAME);
  }
  return [...memoryStore];
}

/**
 * Update transaction status
 */
export async function updateTransactionStatus(id, status, error = null) {
  const db = await getDB();
  if (db) {
    const tx = await db.get(STORE_NAME, id);
    if (tx) {
      tx.status = status;
      tx.lastAttempt = Date.now();
      if (error) {
        tx.error = error;
        tx.retryCount = (tx.retryCount || 0) + 1;
      }
      await db.put(STORE_NAME, tx);
    }
    return;
  }

  const tx = memoryStore.find(t => t.id === id);
  if (tx) {
    tx.status = status;
    tx.lastAttempt = Date.now();
    if (error) {
      tx.error = error;
      tx.retryCount = (tx.retryCount || 0) + 1;
    }
  }
}

/**
 * Mark transaction as synced
 */
export async function markTransactionSynced(id) {
  await updateTransactionStatus(id, 'synced');
}

/**
 * Mark transaction as failed
 */
export async function markTransactionFailed(id, error) {
  await updateTransactionStatus(id, 'failed', error);
}

/**
 * Delete a transaction from the queue
 */
export async function deleteTransaction(id) {
  const db = await getDB();
  if (db) {
    await db.delete(STORE_NAME, id);
    return;
  }
  memoryStore = memoryStore.filter(t => t.id !== id);
}

/**
 * Clear all synced transactions (cleanup)
 */
export async function clearSyncedTransactions() {
  const db = await getDB();
  if (db) {
    const txs = await db.getAllFromIndex(STORE_NAME, 'status', 'synced');
    for (const tx of txs) {
      await db.delete(STORE_NAME, tx.id);
    }
    console.log('[OfflineDB] Cleared', txs.length, 'synced transactions');
    return;
  }
  const initial = memoryStore.length;
  memoryStore = memoryStore.filter(t => t.status !== 'synced');
  console.log('[OfflineDB] Cleared', initial - memoryStore.length, 'synced transactions');
}

/**
 * Clear failed transactions
 */
export async function clearFailedTransactions() {
  const db = await getDB();
  if (db) {
    const txs = await db.getAllFromIndex(STORE_NAME, 'status', 'failed');
    for (const tx of txs) {
      await db.delete(STORE_NAME, tx.id);
    }
    return;
  }
  memoryStore = memoryStore.filter(t => t.status !== 'failed');
}

/**
 * Get count of pending transactions
 */
export async function getPendingCount() {
  const db = await getDB();
  if (db) {
    return db.countFromIndex(STORE_NAME, 'status', 'pending');
  }
  return memoryStore.filter(t => t.status === 'pending').length;
}

/**
 * Get count of failed transactions
 */
export async function getFailedCount() {
  const db = await getDB();
  if (db) {
    return db.countFromIndex(STORE_NAME, 'status', 'failed');
  }
  return memoryStore.filter(t => t.status === 'failed').length;
}

/**
 * Retry failed transactions (reset status to pending)
 */
export async function retryFailedTransactions() {
  const db = await getDB();
  if (db) {
    const failedTxs = await db.getAllFromIndex(STORE_NAME, 'status', 'failed');
    for (const tx of failedTxs) {
      tx.status = 'pending';
      tx.error = null;
      await db.put(STORE_NAME, tx);
    }
    console.log('[OfflineDB] Retrying', failedTxs.length, 'failed transactions');
    return failedTxs.length;
  }

  const failed = memoryStore.filter(t => t.status === 'failed');
  failed.forEach(t => {
    t.status = 'pending';
    t.error = null;
  });
  return failed.length;
}

/**
 * Check if we're online
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Listen for online/offline events
 */
export function setupOnlineListener(onOnline, onOffline) {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}